import React, { createContext, useState, useEffect, useContext } from "react";
import { authService } from "../services/authService";
import {
  resolveInitialPostId,
  setStoredPostId,
  clearStoredPostId,
  findPost
} from "../services/postSelection";
import { refreshRestrictedAssignedQueriesPostBases } from "../services/restrictedPostsConfig";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activePostId, setActivePostId] = useState(null);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("accessToken");
      const storedUser = localStorage.getItem("user");

      if (token && storedUser) {
        try {
          // Set user from localStorage immediately for faster rendering
          // while we verify with API in background
          try {
            const parsedUser =
              typeof storedUser === "string"
                ? JSON.parse(storedUser)
                : storedUser;
            if (parsedUser && parsedUser.role) {
              setUser(parsedUser);
              setIsAuthenticated(true);
              setActivePostId(resolveInitialPostId(parsedUser));
              // console.log('[AuthContext] Loaded user from localStorage:', { role: parsedUser.role, email: parsedUser.email });
            }
          } catch (parseError) {
            // console.error('[AuthContext] Failed to parse stored user:', parseError);
          }

          // Verify token is still valid by fetching user data (in background)
          try {
            const response = await authService.getCurrentUser();
            if (response.success && response.data.user) {
              // Update user with fresh data from API
              setUser(response.data.user);
              setIsAuthenticated(true);
              setActivePostId(resolveInitialPostId(response.data.user));
              localStorage.setItem("user", JSON.stringify(response.data.user));
              // console.log('[AuthContext] Updated user from API:', { role: response.data.user.role, email: response.data.user.email });
            } else {
              // API returned failure - token might be invalid
              // console.warn('[AuthContext] API verification failed, clearing auth');
              setUser(null);
              setIsAuthenticated(false);
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("user");
              localStorage.removeItem("ssoToken");
            }
          } catch (apiError) {
            // API call failed - could be network issue
            // Don't clear auth if it's just a network error (4xx/5xx might be auth issues)
            // console.warn('[AuthContext] API verification error:', apiError.message);
            // Only clear if it's an auth error (401/403), not network errors
            if (
              apiError.response?.status === 401 ||
              apiError.response?.status === 403
            ) {
              setUser(null);
              setIsAuthenticated(false);
              localStorage.removeItem("accessToken");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("user");
              localStorage.removeItem("ssoToken");
            }
            // Otherwise, keep the localStorage user (might be network issue)
          }
        } catch (error) {
          // console.error('[AuthContext] Auth check error:', error);
          // Only clear if it's a critical error
          if (
            error.response?.status === 401 ||
            error.response?.status === 403
          ) {
            localStorage.removeItem("accessToken");
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("user");
            localStorage.removeItem("ssoToken");
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Refreshes the Assigned-Queries restricted-post config (see
  // services/restrictedPostsConfig.js) once per session — non-blocking, the
  // backend enforces this independently regardless of whether this succeeds.
  useEffect(() => {
    if (isAuthenticated) {
      refreshRestrictedAssignedQueriesPostBases();
    }
  }, [isAuthenticated]);

  // Manual login disabled — application now uses SSO only

  const login = async (email, password) => {
    try {
      const response = await authService.login(email, password);
      if (response.success) {
        const { user: userData, accessToken, refreshToken } = response.data;
        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("user", JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        setActivePostId(resolveInitialPostId(userData));
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      const status = error.response?.status;
      const remainingHeader = error.response?.headers?.["ratelimit-remaining"];
      const remainingFromHeader = remainingHeader
        ? parseInt(remainingHeader, 10)
        : null;
      if (status === 429) {
        const retryAfterHeader = error.response?.headers?.["retry-after"];
        const retryAfterSecondsFromHeader = retryAfterHeader
          ? parseInt(retryAfterHeader, 10)
          : null;
        const retryAfterSeconds =
          error.response?.data?.retryAfterSeconds ||
          (Number.isFinite(retryAfterSecondsFromHeader)
            ? retryAfterSecondsFromHeader
            : null);
        return {
          success: false,
          status,
          retryAfterSeconds,
          error:
            error.response?.data?.message ||
            "Too many login attempts. Please try again later."
        };
      }
      return {
        success: false,
        status,
        remainingAttempts: Number.isFinite(remainingFromHeader)
          ? remainingFromHeader
          : null,
        error:
          error.response?.data?.message || "Login failed. Please try again."
      };
    }
  };

  /*
  const register = async (name, email, password) => {
    try {
      const response = await authService.register(name, email, password);
      if (response.success) {
        const { user: userData, accessToken, refreshToken } = response.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
        setIsAuthenticated(true);
        return { success: true };
      }
      return { success: false, error: response.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed. Please try again.',
      };
    }
  };
  */

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
    setActivePostId(null);
    clearStoredPostId();
    // Clear all auth data
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("user");
    localStorage.removeItem("ssoToken");
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  // Switches the active post. Only accepts a postId that's actually one of
  // the current user's own posts (mirrors the server-side check in
  // utils/postScope.js's resolveActivePostId) — a no-op otherwise.
  const setActivePost = (postId) => {
    if (!findPost(user, postId)) {
      return false;
    }
    setActivePostId(postId);
    setStoredPostId(postId);
    return true;
  };

  const loginWithSSO = async (ssoData) => {
    try {
      // Log the incoming data for debugging (only in development)
      if (process.env.NODE_ENV === "development") {
        // console.log('loginWithSSO called with data:', ssoData);
      }

      // Validate the data structure
      if (!ssoData) {
        throw new Error("SSO data is missing");
      }

      const {
        user: rawUserData,
        ourTokens,
        ssoToken,
        accessRestricted,
        accessStatus
      } = ssoData;
      const userData = rawUserData;

      // Validate required fields
      if (!userData) {
        throw new Error("User data is missing from SSO response");
      }

      if (!ourTokens || !ourTokens.accessToken || !ourTokens.refreshToken) {
        throw new Error("Authentication tokens are missing from SSO response");
      }

      if (!ssoToken || !ssoToken.token) {
        throw new Error("SSO token is missing from response");
      }

      // Store our JWT tokens
      localStorage.setItem("accessToken", ourTokens.accessToken);
      localStorage.setItem("refreshToken", ourTokens.refreshToken);

      // Store SSO token
      const token = ssoToken.token;

      // Store raw token
      localStorage.setItem("ssoToken", token);

      try {
        // Step 1: Split JWT → [header, payload, signature]
        const base64Url = token.split(".")[1];

        // Step 2: Convert Base64URL → Base64
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");

        // Step 3: Decode
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );

        const decoded = JSON.parse(jsonPayload);

        // Step 4: Parse AccessRights (string → object)
        const accessRights = JSON.parse(decoded.AccessRights || "{}");

        // Store in localStorage
        localStorage.setItem("AccessRights", JSON.stringify(accessRights));
        localStorage.setItem("userData", JSON.stringify(decoded));
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Token decode failed:", error);
      }

      // Access pending restriction disabled — users allowed even if role not mapped.
      /*
      // Store access restriction status if present
      if (accessRestricted) {
        localStorage.setItem('accessRestricted', 'true');
        localStorage.setItem('accessStatus', accessStatus || 'pending_admin_approval');
      }
      */

      // Store user data - use try-catch for JSON.stringify
      try {
        const userString = JSON.stringify(userData);
        localStorage.setItem("user", userString);
      } catch (stringifyError) {
        // console.error('Failed to stringify user data:', stringifyError);
        throw new Error("Failed to save user data: " + stringifyError.message);
      }

      setUser(userData);
      setIsAuthenticated(true);
      setActivePostId(resolveInitialPostId(userData));

      if (process.env.NODE_ENV === "development") {
        // console.log('loginWithSSO completed successfully', { accessRestricted, accessStatus });
      }

      return {
        success: true,
        accessRestricted,
        accessStatus
      };
    } catch (error) {
      // console.error('loginWithSSO error:', error);
      return {
        success: false,
        error: error.message || "SSO login failed"
      };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    // register,
    logout,
    updateUser,
    loginWithSSO,
    // Post-based project scoping
    posts: user?.posts ?? [],
    activePostId,
    setActivePost
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
